import React, { useEffect, useState } from 'react';
import { CURRENT_VERSION, RELEASE_NOTES } from '../../data/releaseNotes';

const SEEN_KEY = `dash-whatsnew-seen`;

const TAG_STYLES = {
  New:      { bg: 'rgba(29,156,156,0.12)',   color: '#0d7377' },
  Improved: { bg: 'rgba(99,102,241,0.12)',   color: '#4f46e5' },
  Fix:      { bg: 'rgba(220,38,38,0.10)',    color: '#b91c1c' },
};

// One-time keyframe injection for banner shimmer / drawer slide
if (typeof document !== 'undefined' && !document.getElementById('wn-keyframes')) {
  const el = document.createElement('style');
  el.id = 'wn-keyframes';
  el.textContent = `
    @keyframes wnShimmer { 0%,100% { box-shadow: 0 0 0 0 rgba(29,156,156,0.0); }
                           50%      { box-shadow: 0 0 0 4px rgba(29,156,156,0.12); } }
    @keyframes wnSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
    @keyframes wnFadeIn  { from { opacity: 0; } to { opacity: 1; } }
    @keyframes wnArrow   { 0%,100% { transform: translateX(0); } 50% { transform: translateX(4px); } }
  `;
  document.head.appendChild(el);
}

export default function WhatsNewBanner() {
  const [seen, setSeen] = useState(() => {
    try { return localStorage.getItem(SEEN_KEY) === CURRENT_VERSION; } catch { return true; }
  });
  const [open, setOpen] = useState(false);

  const dismiss = () => {
    setSeen(true);
    try { localStorage.setItem(SEEN_KEY, CURRENT_VERSION); } catch {}
  };

  const openDrawer = () => {
    setOpen(true);
    dismiss(); // viewing the notes also dismisses the highlight
  };

  // Close drawer on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      {/* Slim banner — visible until dismissed/viewed */}
      {!seen && (
        <div
          role="button"
          tabIndex={0}
          onClick={openDrawer}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openDrawer(); }}
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 14,
            padding: '10px 56px 10px 24px',
            background: 'linear-gradient(135deg, #0d4f52 0%, #1d9c9c 60%, #4fd1c5 100%)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            animation: 'wnShimmer 3.5s ease-in-out infinite',
            letterSpacing: '0.01em',
            userSelect: 'none',
          }}
          title="See what's new in DASH"
        >
          <span style={{
            padding: '3px 9px',
            borderRadius: 999,
            background: 'rgba(255,255,255,0.18)',
            border: '1px solid rgba(255,255,255,0.35)',
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}>
            What's New
          </span>
          <span>
            We've been listening. DASH just got better.
          </span>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontWeight: 700,
            animation: 'wnArrow 1.6s ease-in-out infinite',
          }}>
            See what's new →
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); dismiss(); }}
            title="Dismiss"
            aria-label="Dismiss what's new banner"
            style={{
              position: 'absolute',
              right: 14,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.18)',
              color: '#fff',
              border: 'none',
              fontSize: 14,
              lineHeight: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >×</button>
        </div>
      )}

      {/* Drawer — release notes panel */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setOpen(false)}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(15,23,42,0.45)',
              backdropFilter: 'blur(2px)',
              zIndex: 950,
              animation: 'wnFadeIn 0.18s ease',
            }}
          />
          {/* Panel */}
          <aside
            style={{
              position: 'fixed',
              top: 0, right: 0, bottom: 0,
              width: 'min(420px, 92vw)',
              background: '#ffffff',
              boxShadow: '-8px 0 40px rgba(13,79,82,0.22)',
              zIndex: 951,
              animation: 'wnSlideIn 0.28s cubic-bezier(0.34,1.4,0.64,1)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '18px 22px',
              background: 'linear-gradient(135deg, #0d4f52, #1d9c9c)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>
                  What's New in DASH
                </div>
                <div style={{ fontSize: 11.5, opacity: 0.85, marginTop: 2 }}>
                  We're constantly listening to customer feedback.
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'rgba(255,255,255,0.18)',
                  color: '#fff', border: 'none',
                  fontSize: 18, lineHeight: 1, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >×</button>
            </div>

            {/* Body — scrollable */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '22px 22px 32px' }}>
              {RELEASE_NOTES.map((entry, idx) => (
                <div key={entry.date} style={{ marginBottom: idx === RELEASE_NOTES.length - 1 ? 0 : 28 }}>
                  <div style={{ marginBottom: 4 }}>
                    <div style={{ fontSize: 15.5, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' }}>
                      {entry.title}
                    </div>
                  </div>
                  {entry.summary && (
                    <div style={{ fontSize: 13, color: '#475569', marginBottom: 12, lineHeight: 1.55 }}>
                      {entry.summary}
                    </div>
                  )}
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {entry.items.map((it, i) => {
                      const ts = TAG_STYLES[it.tag] || { bg: 'rgba(100,116,139,0.12)', color: '#475569' };
                      return (
                        <li key={i} style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 10,
                          padding: '8px 10px',
                          borderRadius: 8,
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                        }}>
                          <span style={{
                            flexShrink: 0,
                            padding: '2px 8px',
                            borderRadius: 999,
                            background: ts.bg,
                            color: ts.color,
                            fontSize: 10,
                            fontWeight: 800,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                          }}>
                            {it.tag || '•'}
                          </span>
                          <span style={{ flex: 1, fontSize: 13, color: '#0f172a', lineHeight: 1.55 }}>
                            {it.text}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}

            </div>
          </aside>
        </>
      )}
    </>
  );
}
